import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminSidebar from '../components/AdminSidebar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  client: 'bg-blue-100 text-blue-700',
  provider: 'bg-purple-100 text-purple-700',
  admin: 'bg-red-100 text-red-700',
};

export default function AdminUsers() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [duplicateEmails, setDuplicateEmails] = useState(new Set());
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showBanForm, setShowBanForm] = useState(false);
  const [banReason, setBanReason] = useState('');

  const roleLabel = (role) => ({
    client: t('admin.rolesClient', 'Cliente'),
    provider: t('admin.rolesProvider', 'Proveedor'),
    admin: t('admin.rolesAdmin', 'Administrador'),
  }[role] || role);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' })
      .then(r => { if (r.status === 401 || r.status === 403) navigate('/login'); return r.json(); })
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setUsers(list);

        // Detectar duplicados localmente (mismo email ignorando capitalización)
        const emailCount = {};
        list.forEach(u => {
          const key = u.email?.toLowerCase().trim();
          emailCount[key] = (emailCount[key] || 0) + 1;
        });
        const dupSet = new Set(
          Object.entries(emailCount).filter(([, c]) => c > 1).map(([k]) => k)
        );
        setDuplicateEmails(dupSet);
        setDuplicateCount(dupSet.size);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchVerified =
      verifiedFilter === 'all' ||
      (verifiedFilter === 'yes' && u.verified) ||
      (verifiedFilter === 'no' && !u.verified);
    return matchSearch && matchRole && matchVerified;
  });

  const isDuplicate = (u) => duplicateEmails.has(u.email?.toLowerCase().trim());

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  const apiPatch = async (url, body) => {
    const res = await fetch(`${BACKEND_URL}${url}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const apiDelete = async (url) => {
    const res = await fetch(`${BACKEND_URL}${url}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return res.json();
  };

  const apiPost = async (url, body) => {
    const res = await fetch(`${BACKEND_URL}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.json();
  };

  const handleToggleVerify = async (userId, currentVerified) => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const data = await apiPatch(`/api/admin/users/${userId}/verify`, { verified: !currentVerified });
      if (data.user) {
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...data.user } : u));
        setSelected(prev => prev ? { ...prev, ...data.user } : null);
        setActionMsg({ type: 'success', text: !currentVerified ? '✅ Usuario verificado correctamente' : '⏳ Verificación removida' });
      } else {
        setActionMsg({ type: 'error', text: data.detail || 'Error al actualizar' });
      }
    } catch {
      setActionMsg({ type: 'error', text: 'Error de red' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const data = await apiPatch(`/api/admin/users/${userId}/role`, { role: newRole });
      if (data.user) {
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...data.user } : u));
        setSelected(prev => prev ? { ...prev, ...data.user } : null);
        setActionMsg({ type: 'success', text: `✅ Rol cambiado a ${newRole}` });
      } else {
        setActionMsg({ type: 'error', text: data.detail || 'Error al cambiar rol' });
      }
    } catch {
      setActionMsg({ type: 'error', text: 'Error de red' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBanUser = async (userId) => {
    if (!banReason.trim()) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const data = await apiPost(`/api/admin/users/${userId}/ban`, { reason: banReason.trim() });
      if (data.user) {
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...data.user } : u));
        setSelected(prev => prev ? { ...prev, ...data.user } : null);
        setActionMsg({ type: 'success', text: '🚫 Usuario suspendido' });
        setShowBanForm(false);
        setBanReason('');
      } else {
        setActionMsg({ type: 'error', text: data.detail || 'No se pudo suspender' });
      }
    } catch {
      setActionMsg({ type: 'error', text: 'Error de red' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnbanUser = async (userId) => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const data = await apiPost(`/api/admin/users/${userId}/unban`);
      if (data.user) {
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...data.user } : u));
        setSelected(prev => prev ? { ...prev, ...data.user } : null);
        setActionMsg({ type: 'success', text: '✅ Usuario reactivado' });
      } else {
        setActionMsg({ type: 'error', text: data.detail || 'No se pudo reactivar' });
      }
    } catch {
      setActionMsg({ type: 'error', text: 'Error de red' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const data = await apiDelete(`/api/admin/users/${userId}`);
      if (data.message && !data.detail) {
        setUsers(prev => prev.filter(u => u.user_id !== userId));
        setSelected(null);
        setConfirmDelete(false);
        fetchUsers(); // refrescar duplicados
      } else {
        setActionMsg({ type: 'error', text: data.detail || 'No se pudo eliminar' });
        setConfirmDelete(false);
      }
    } catch {
      setActionMsg({ type: 'error', text: 'Error de red' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1">
        <AdminTabs />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <button onClick={() => navigate('/admin/dashboard')} className="mb-6 text-sm text-gray-500 hover:text-[#134168] transition-colors">
          {t('admin.backToPanelShort', '← Panel')}
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-heading font-bold text-[#134168]">{t('admin.usersMgmt', 'Gestión de Usuarios')}</h1>
          <span className="text-sm text-gray-500">{t('admin.totalUsersCount', { n: users.length }, `${users.length} usuarios`)}</span>
        </div>

        {/* Banner de duplicados */}
        {duplicateCount > 0 && (
          <div className="mb-5 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
            <span className="text-xl mt-0.5">⚠️</span>
            <div>
              <p className="font-semibold text-amber-800 text-sm">
                Se detectaron <strong>{duplicateCount}</strong> correo{duplicateCount > 1 ? 's' : ''} con usuarios duplicados
              </p>
              <p className="text-amber-700 text-xs mt-0.5">
                Las filas resaltadas en amarillo comparten el mismo email con diferente capitalización. Elimina el duplicado desde el detalle del usuario.
              </p>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 mb-6">
          <input
            type="text"
            placeholder={t('admin.searchUsersPlaceholder', 'Buscar por nombre o email...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-[180px] px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-sm"
          />
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168] bg-white"
          >
            <option value="all">{t('admin.allRoles', 'Todos los roles')}</option>
            <option value="client">{t('admin.rolesClients', 'Clientes')}</option>
            <option value="provider">{t('admin.rolesProviders', 'Proveedores')}</option>
            <option value="admin">{t('admin.rolesAdmins', 'Admins')}</option>
          </select>
          <select
            value={verifiedFilter}
            onChange={e => setVerifiedFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168] bg-white"
          >
            <option value="all">Todos</option>
            <option value="yes">✅ Verificados</option>
            <option value="no">⏳ Sin verificar</option>
          </select>
        </div>

        {loading ? (
          <AdminLoadingScreen chrome={false} className="py-16" message={t('admin.loadingUsers', 'Cargando usuarios...')} />
        ) : (
          <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {[t('admin.name', 'Nombre'), t('admin.emailLabel', 'Correo'), t('admin.role', 'Rol'), t('admin.verified', 'Verificado'), t('admin.registered', 'Registrado')].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-12 text-gray-400">{t('admin.noUsers', 'Sin usuarios')}</td></tr>
                  ) : filtered.map(u => {
                    const dup = isDuplicate(u);
                    return (
                      <tr
                        key={u.user_id}
                        onClick={() => { setSelected(u); setActionMsg(null); setConfirmDelete(false); }}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${dup ? 'bg-amber-50' : ''}`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {u.name}
                          {dup && <span className="ml-2 text-xs text-amber-600 font-semibold">⚠️ Duplicado</span>}
                          {u.is_banned && <span className="ml-2 text-xs text-red-600 font-semibold">🚫 Suspendido</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                            {roleLabel(u.role)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{u.verified ? '✅' : '⏳'}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString(locale) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal de detalle enriquecido */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl p-6 md:p-8 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-xl font-heading font-bold">{selected.name}</h2>
                <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[selected.role] || 'bg-gray-100 text-gray-600'}`}>
                  {roleLabel(selected.role)}
                </span>
                {isDuplicate(selected) && (
                  <span className="ml-2 inline-block mt-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    ⚠️ Email duplicado
                  </span>
                )}
                {selected.is_banned && (
                  <span className="ml-2 inline-block mt-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    🚫 Suspendido
                  </span>
                )}
              </div>
              <button onClick={() => setSelected(null)} aria-label="Cerrar" className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2">×</button>
            </div>

            {/* Mensaje de acción */}
            {actionMsg && (
              <div className={`mb-4 text-sm rounded-xl px-4 py-3 ${actionMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {actionMsg.text}
              </div>
            )}

            {/* Datos del usuario */}
            <dl className="space-y-3 text-sm mb-5">
              {[
                [t('admin.emailLabel', 'Correo'), selected.email],
                [t('admin.detailPhone', 'Teléfono'), selected.phone_whatsapp || '—'],
                [t('admin.detailDocument', 'Documento'), selected.document_id || '—'],
                [t('admin.verified', 'Verificado'), selected.verified ? '✅ Sí' : '⏳ No'],
                ['Email verificado', selected.email_verified ? '✅ Sí' : '⏳ No'],
                [t('admin.registered', 'Registrado'), selected.created_at ? new Date(selected.created_at).toLocaleDateString(locale) : '—'],
                ['ID de usuario', selected.user_id],
                ...(selected.is_banned ? [['Motivo de suspensión', selected.ban_reason || '—']] : []),
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 border-b border-gray-50 pb-2">
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="font-medium text-right break-all">{value}</dd>
                </div>
              ))}
            </dl>

            {/* Cuenta de pago del proveedor */}
            {selected.role === 'provider' && (
              <div className="mb-5">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">{t('payout.title', 'Cuenta de Pago')}</h3>
                {selected.payout_account ? (
                  <dl className="space-y-3 text-sm bg-gray-50 rounded-2xl p-4">
                    {(selected.payout_account.method === 'paypal' ? [
                      [t('payout.methodLabel', 'Método'), 'PayPal'],
                      [t('payout.holderName', 'Titular'), selected.payout_account.holder_name],
                      [t('payout.paypalEmail', 'PayPal Email'), selected.payout_account.paypal_email],
                    ] : [
                      [t('payout.methodLabel', 'Método'), t('payout.methodBank', 'Banco')],
                      [t('payout.holderName', 'Titular'), selected.payout_account.holder_name],
                      [t('payout.bankName', 'Banco'), selected.payout_account.bank_name],
                      [t('payout.accountType', 'Tipo'), selected.payout_account.account_type === 'corriente' ? t('payout.accountCorriente', 'Corriente') : t('payout.accountAhorro', 'Ahorro')],
                      [t('payout.accountNumber', 'Número de cuenta'), selected.payout_account.account_number],
                    ]).map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-4">
                        <dt className="text-gray-500">{label}</dt>
                        <dd className="font-medium text-right break-all">{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-sm text-gray-400 bg-gray-50 rounded-2xl p-4">{t('payout.notConfigured', 'Sin cuenta configurada')}</p>
                )}
              </div>
            )}

            {/* Fotos de Identidad / KYC */}
            {(selected.document_image_front || selected.document_image_back) && (
              <div className="mb-5 border-t border-gray-100 pt-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                  Documentos de Identidad (KYC)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {selected.document_image_front && (
                    <div className="space-y-1">
                      <span className="text-[11px] font-semibold text-gray-500">Frente (Anverso)</span>
                      <a href={selected.document_image_front} target="_blank" rel="noreferrer" className="block relative group overflow-hidden rounded-xl border border-gray-200">
                        <img src={selected.document_image_front} alt="Frente" className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                        <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold">
                          Ver grande
                        </span>
                      </a>
                    </div>
                  )}
                  {selected.document_image_back && (
                    <div className="space-y-1">
                      <span className="text-[11px] font-semibold text-gray-500">Reverso (Atrás)</span>
                      <a href={selected.document_image_back} target="_blank" rel="noreferrer" className="block relative group overflow-hidden rounded-xl border border-gray-200">
                        <img src={selected.document_image_back} alt="Reverso" className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                        <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold">
                          Ver grande
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Acciones del Admin ── */}
            {selected.role !== 'admin' && (
              <div className="border-t border-gray-100 pt-5 space-y-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Acciones de administrador</h3>

                {/* Verificar / Desverificar */}
                <button
                  id={`btn-verify-${selected.user_id}`}
                  onClick={() => handleToggleVerify(selected.user_id, selected.verified)}
                  disabled={actionLoading}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                    selected.verified
                      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                  }`}
                >
                  {actionLoading ? 'Procesando...' : selected.verified ? '⏳ Quitar verificación' : '✅ Verificar cuenta manualmente'}
                </button>

                {/* Cambiar rol (solo entre client/provider) */}
                {selected.role !== 'admin' && (
                  <div className="flex gap-2">
                    <span className="text-sm text-gray-500 self-center whitespace-nowrap">Cambiar rol a:</span>
                    {selected.role !== 'client' && (
                      <button
                        id={`btn-role-client-${selected.user_id}`}
                        onClick={() => handleChangeRole(selected.user_id, 'client')}
                        disabled={actionLoading}
                        className="flex-1 py-2 rounded-xl text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors disabled:opacity-50"
                      >
                        Cliente
                      </button>
                    )}
                    {selected.role !== 'provider' && (
                      <button
                        id={`btn-role-provider-${selected.user_id}`}
                        onClick={() => handleChangeRole(selected.user_id, 'provider')}
                        disabled={actionLoading}
                        className="flex-1 py-2 rounded-xl text-sm font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 transition-colors disabled:opacity-50"
                      >
                        Proveedor
                      </button>
                    )}
                  </div>
                )}

                {/* Suspender / Reactivar */}
                {selected.is_banned ? (
                  <button
                    id={`btn-unban-${selected.user_id}`}
                    onClick={() => handleUnbanUser(selected.user_id)}
                    disabled={actionLoading}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Procesando...' : '✅ Reactivar cuenta'}
                  </button>
                ) : !showBanForm ? (
                  <button
                    id={`btn-ban-${selected.user_id}`}
                    onClick={() => { setShowBanForm(true); setBanReason(''); }}
                    disabled={actionLoading}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
                  >
                    🚫 Suspender cuenta
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
                    <label className="text-sm text-red-700 font-medium block">Motivo de la suspensión</label>
                    <textarea
                      id={`ban-reason-${selected.user_id}`}
                      value={banReason}
                      onChange={e => setBanReason(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                      placeholder="Ej: incumplimiento de términos de servicio"
                    />
                    <div className="flex gap-2 pt-1">
                      <button
                        id={`btn-confirm-ban-${selected.user_id}`}
                        onClick={() => handleBanUser(selected.user_id)}
                        disabled={actionLoading || !banReason.trim()}
                        className="flex-1 py-2 rounded-xl text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {actionLoading ? 'Procesando...' : 'Confirmar suspensión'}
                      </button>
                      <button
                        onClick={() => { setShowBanForm(false); setBanReason(''); }}
                        className="flex-1 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {/* Eliminar usuario */}
                {!confirmDelete ? (
                  <button
                    id={`btn-delete-${selected.user_id}`}
                    onClick={() => setConfirmDelete(true)}
                    disabled={actionLoading}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
                  >
                    🗑️ Eliminar usuario
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
                    <p className="text-sm text-red-700 font-medium">¿Confirmas que deseas eliminar a <strong>{selected.name}</strong>?</p>
                    <p className="text-xs text-red-500">Esta acción es irreversible. Solo es posible si no tiene reservas activas.</p>
                    <div className="flex gap-2 pt-1">
                      <button
                        id={`btn-confirm-delete-${selected.user_id}`}
                        onClick={() => handleDeleteUser(selected.user_id)}
                        disabled={actionLoading}
                        className="flex-1 py-2 rounded-xl text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {actionLoading ? 'Eliminando...' : 'Sí, eliminar'}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(false)}
                        className="flex-1 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
